import { useServiceCenterInfoByEventNameQuery } from '@graphql/generated';
import { useEventInfo } from '@shared/utils/eventInfo';
import { RemoteFeatureFlagKeys, RemoteFeatureFlagOptions, useFeatureValue } from '@shared/core/featureFlags';
import { useMemo } from 'react';

const rsvpExperiments: RemoteFeatureFlagKeys[] = ['rsvpEnableAccommodationsRedirect', 'rsvpGuestFlowAccommodationsStep', 'postRSVPEnableNearbyHotelsCard'];

/**
 * controls some amplitude ff based on an event feature flag
 * https://withjoy.atlassian.net/wiki/spaces/LNCH/pages/3779297324/OptOutRsvpExperiments
 */
export const useRsvpFeatureValue = (featureName: RemoteFeatureFlagKeys, options?: RemoteFeatureFlagOptions) => {
  const { eventHandle } = useEventInfo();

  const { data: featureFlagData, called, loading } = useServiceCenterInfoByEventNameQuery({ variables: { eventName: eventHandle! }, batchMode: 'fast', skip: !eventHandle });

  const eventFlagsLoaded = called && !loading;

  const rsvpExperimentsDisabled = !!useMemo(() => featureFlagData?.eventByName?.featureFlags?.includes('optOutRsvpExperiments'), [featureFlagData?.eventByName?.featureFlags]);
  const isControlledByEventFlag = rsvpExperiments.includes(featureName);

  const shouldSkipAmplitudeAssignment = useMemo(() => {
    if (!eventFlagsLoaded) {
      return true;
    }

    if (isControlledByEventFlag && rsvpExperimentsDisabled) {
      return true;
    }

    return options?.skip ?? false;
  }, [eventFlagsLoaded, isControlledByEventFlag, options?.skip, rsvpExperimentsDisabled]);

  return useFeatureValue(featureName, { skip: shouldSkipAmplitudeAssignment });
};
